<script lang="ts">
import { ACCEPTED_FILE_DOCUMENT_TYPES } from '@/types/AcceptedTypes';
export default {
    name: 'DropArea',
};
</script>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import AppButton from '@/components/v2/AppButton.vue';

const props = defineProps<{
  shouldShowOnInit?: boolean;
  isCumulative?: boolean;
  files?: File[];
  title?: string;
  fileChooserTitle?: string;
  acceptedFileTypes?: string[];
}>();

const emit = defineEmits<{
  'update:shouldShowOnInit': [boolean]
  'update:files': [File[]]
  'files-selected': [File[]]
}>();

// -----
// refs
// -----

const isVisible = ref(props.shouldShowOnInit || false);
const listener = ref<(() => void) | undefined>(undefined);
const selectedFiles = ref<File[]>([]);
const file = ref<HTMLInputElement | null>(null);

// ---------
// computed
// ---------

const fileTypes = computed(() => {
  return props.acceptedFileTypes?.join(',') || ACCEPTED_FILE_DOCUMENT_TYPES;
});

// ----------------
// lifecycle hooks
// ----------------

onMounted(() => {
  listener.value = () => {
    isVisible.value = true;
    emit('update:shouldShowOnInit', isVisible.value);
  };
  document.body.addEventListener('dragover', listener.value);
});

onUnmounted(() => {
  if (listener.value) {
    document.body.removeEventListener('dragover', listener.value);
  }
});

// ---------
// watchers
// ---------

watch(() => props.shouldShowOnInit, (shouldShowOnInit) => {
  isVisible.value = shouldShowOnInit;
});

watch(() => props.files, (files) => {
  selectedFiles.value = files || [];
});

// ----------
// functions
// ----------

function handleFileSelect(event: Event) {
  if (!event || !event.target) {
    return;
  }
  const inputFiles = Array.from((event.target as HTMLInputElement).files || []);
  const files: File[] = props.isCumulative ? [...selectedFiles.value] : [];
  for (const file of inputFiles) {
    if (file && !files.find((x) => x.name === file.name)) {
      files.push(file);
    }
  }
  emit('files-selected', files);
  emit('update:files', files);
  selectedFiles.value = files;
}

function handleFileDrop(dropEvent: DragEvent): boolean {
  dropEvent.preventDefault();
  dropEvent.stopPropagation();
  const files: File[] = props.isCumulative ? selectedFiles.value : [];
  const transfer = dropEvent.dataTransfer;
  if (!transfer) {
    return false;
  }
  if (transfer.items) { // browser-dependent 
    for (let i = 0; i < transfer.items.length; i++) {
      if (transfer.items[i].kind === 'file') {
        const file = transfer.items[i].getAsFile();
        if (file && !files.find((x) => x.name === file.name)) {
          files.push(file);
        }
      }
    }
  } else {
    for (let file of transfer.files) {
      files.push(file);
    }
  }
  emit('files-selected', files);
  emit('update:files', files);
  selectedFiles.value = files;
  return false;
}
</script>

<template>
  <div class="drop-area transition-all" v-show="isVisible" @drop="handleFileDrop" @dragover.prevent>
    <input title='' type='file' @change='handleFileSelect' multiple :accept="fileTypes" ref="file" style="display: none" />
    <slot :file="file">
      <div class="drop-text flex items-center flex-col gap-1">
        <p>{{ title || 'Drop files here for upload' }} </p>
        <p>or</p>
        <app-button class="border-1 border-gray-400 border-solid w-fit-content" @click="() => file && file.click()">{{fileChooserTitle || 'Browse' }}</app-button>
      </div>
    </slot>
  </div>
</template>

<style scoped>
  .drop-area {
    background: #f0f0f0;
    cursor: pointer;
    position: relative;
    width: 100%;
    height: 10em;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .drop-area:hover {
    background: #dedede;
  }

  .drop-area > .drop-text {
     font-size: 1.2em;
     text-align: center;
  }
</style>

